import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const features = [
  {
    title: "Engineering Expertise",
    description: "Our team combines technical knowledge with industry experience to deliver dependable solutions.",
    icon: "ShieldCheck"
  },
  {
    title: "Smart Technology Integration",
    description: "We design systems that connect seamlessly with modern building infrastructure.",
    icon: "Activity"
  },
  {
    title: "Performance Reliability",
    description: "Our engineering approach focuses on long-term efficiency and minimal downtime.",
    icon: "Zap"
  },
  {
    title: "End-to-End Support",
    description: "From planning and installation to maintenance and monitoring, we support projects at every stage.",
    icon: "LifeBuoy"
  }
];

const WhyChooseUs = () => {
  return (
    <section className="py-16 md:py-24 bg-white overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <h2 className="text-sm font-bold uppercase tracking-[0.3em] text-[#91756A] mb-8">Why Choose VASTECH</h2>
            <h3 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#4D4C4A] leading-tight mb-12 italic">
              Excellence Built on <span className="text-[#91756A]">Precision</span>.
            </h3>

            <div className="grid sm:grid-cols-2 gap-x-12 gap-y-16">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                >
                  <div className="flex items-center space-x-4 mb-4">
                    <Icon name={feature.icon} size={20} className="text-[#91756A]" />
                    <h4 className="text-lg font-bold text-[#4D4C4A] tracking-tighter uppercase italic">{feature.title}</h4>
                  </div>
                  <p className="text-slate-500 text-sm leading-relaxed border-l-2 border-[#91756A]/20 pl-4 ml-2">
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="relative aspect-square lg:aspect-auto h-full min-h-[400px] lg:min-h-[500px] overflow-hidden"
          >
            <div className="absolute inset-0 bg-[#4D4C4A] p-4 md:p-6 flex items-end overflow-hidden">
              <Image
                src="/assets/images/office-showroom/technician-at-elevator.jpg"
                alt="Engineering Precision"
                className="w-full h-full object-cover opacity-60 grayscale scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#4D4C4A] via-[#4D4C4A]/70 to-transparent"></div>

              <div className="relative w-full text-white p-4 md:p-6">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.5, duration: 0.8 }}
                >
                  <p className="text-xl md:text-2xl lg:text-3xl font-bold italic leading-tight mb-3 md:mb-4">"Committed to delivering world-class engineering standards."</p>
                  <div className="flex items-center space-x-3 md:space-x-4">
                    <div className="w-8 md:w-12 h-px bg-[#91756A]"></div>
                    <span className="text-xs uppercase tracking-widest font-bold">Vastech Engineering Team</span>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Decorative Offset Frame */}
            <div className="absolute lg:-top-6 lg:-right-6 w-full h-full border-2 border-[#91756A]/20 lg:-z-10 pointer-events-none hidden lg:block"></div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
